import { flow } from '@1x2network/flow'
import { BasicLayout, OrientationMap, Orientor, scaleToLongest } from '@1x2network/layout'
import {
    applySpecification,
    ContainerSpecification,
    createSprite,
    Specification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import { LoadingButton, LoadingButtonSpec, Splash, SplashSpecification } from '@1x2network/pixi_splash'
import { Container, Point, Sprite } from 'pixi.js'
import { SignalConnection } from 'typed-signals'
import { SplashPage, SplashPageSpec } from './SplashPage'

/**
 * Splash screen for Crazy Tiki Hold and Win
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class CrazyTikiSplash extends Splash<SplashSpec> {
    private continue: LoadingButton
    private continueOrientor: Orientor<LoadingButtonSpec>
    private layout: BasicLayout
    private background: Orientor<Sprite>
    private logo: Sprite
    private logoOrientor: Orientor<ContainerSpecification>
    private onResizeConnection: SignalConnection

    constructor(options: { parent?: Container; layout: BasicLayout }) {
        const spec = Specification.get('splash') as SplashSpec
        super({
            spec,
            parent: options.parent,
            pages: [new SplashPage({ spec: spec.page, layout: options.layout })],
        })

        this.layout = options.layout
        this.background = new Orientor({
            layout: options.layout,
            spec: this.specification.background,
            create: (spec: SpriteSpecification) => createSprite({ spec, parent: this }),
            onOrient: (active, inactive) => {
                active.visible = true
                inactive.visible = false
            },
        })

        this.continue = new LoadingButton({ spec: this.specification.continue.landscape, parent: this })
        this.continueOrientor = new Orientor({
            layout: options.layout,
            property: this.specification.continue,
            onOrient: (active) => applySpecification(this.continue, active),
        })

        this.logo = createSprite({ spec: this.specification.logo, parent: this })
        this.logoOrientor = new Orientor({
            layout: options.layout,
            property: this.specification.logo,
            onOrient: (active) => applySpecification(this.logo, active),
        })

        this.onResizeConnection = options.layout.onResize.connect(this.onResize.bind(this))
    }

    private onResize(): void {
        const currentBackground = this.background.active
        currentBackground.scale.set(scaleToLongest(currentBackground.getLocalBounds(), this.layout.size))

        const { left, right, top, bottom } = this.layout
        const midPoint = new Point((left + right) / 2 - this.x, (top + bottom) / 2 - this.y)
        currentBackground.position.copyFrom(midPoint)
    }

    public setLoadProgress(progress: number): void {
        this.continue.setProgress(progress)
    }

    public loadComplete(): void {
        this.continue.cursor = 'pointer'
    }

    public async waitToDismiss(): Promise<void> {
        await flow.wait(this.continue.onPressed)

        // Skip the splash screen animations
        flow.skipStart()
        // Wait for the splash screen animations to finish (should be immediate)
        await this.currentPage?.hide()
        flow.skipStop()

        // Cleanup
        this.dismiss()
        this.background.disconnect()
        this.continueOrientor.disconnect()
        this.onResizeConnection.disconnect()
        this.logo.destroy()
        this.logoOrientor.disconnect()
    }
}

export interface SplashSpec extends SplashSpecification {
    logo: SpriteSpecification & OrientationMap<ContainerSpecification>
    background: OrientationMap<SpriteSpecification>
    continue: OrientationMap<LoadingButtonSpec>
    page: SplashPageSpec
}
